import { set, map, pick } from 'lodash/fp';
import { ASSIGN } from 'constants/actionTypes';
import { TYPES, routes, EVENTS } from 'constants/config';
import request from 'utils/request_ajax';
import { hasFeatureFlags } from 'utils/features';
import { buildAssignment } from './helpers';
import { isAssignee } from '../../reducers/assign';

export const actions = {
  activeStep: step => ({
    type: ASSIGN.STEP.SET_ACTIVE,
    step,
  }),
  resetData: () => ({
    type: ASSIGN.DATA.RESET,
  }),
  requestData: () => ({
    type: ASSIGN.DATA.REQUEST,
  }),
  requestNextPageSuccess: payload => ({
    type: ASSIGN.DATA.REQUEST_NEXT_PAGE_SUCCESS,
    payload,
  }),
  requestDataSuccess: payload => ({
    type: ASSIGN.DATA.REQUEST_SUCCESS,
    payload,
  }),
  requestJourneysSuccess: payload => ({
    type: ASSIGN.DATA.REQUEST_JOURNEYS_SUCCESS,
    payload,
  }),
  updateQuery: query => ({
    type: ASSIGN.PARAMS.UPDATE_QUERY,
    query,
  }),
  updateSort: option => ({
    type: ASSIGN.PARAMS.UPDATE_SORT,
    option,
  }),
  updateJourneySort: option => ({
    type: ASSIGN.PARAMS.UPDATE_JOURNEY_SORT,
    option,
  }),
  updateType: values => ({
    type: ASSIGN.PARAMS.UPDATE_FILTER_TYPE,
    values,
  }),
  selectItem: item => ({
    type: ASSIGN.ITEM.SELECT,
    item,
  }),
  deselectItem: item => ({
    type: ASSIGN.ITEM.DESELECT,
    item,
  }),
  setAssigneesCount: count => ({
    type: ASSIGN.ITEM.SET_ASSIGNEES_COUNT,
    count,
  }),
  updateSchedule: changes => ({
    type: ASSIGN.SCHEDULE.UPDATE,
    changes,
  }),
  updateExistingAssignmentOption: option => ({
    type: ASSIGN.REVIEW.UPDATE_EXISTING_ASSIGNMENT_OPTION,
    option,
  }),
  updateMessage: body => ({
    type: ASSIGN.REVIEW.UPDATE_MESSAGE,
    body,
  }),
  setContentType: contentType => ({
    type: ASSIGN.CONTENT.SET_TYPE,
    contentType,
  }),

  setScheduleStep: schedule => ({
    type: ASSIGN.SCHEDULE.STEP_SELECT,
    schedule,
  }),
};

function sortJourneys(jourenysData, option) {
  if (option.type === 'title') {
    if (option.order === 'ASC') {
      jourenysData.sort((a, b) => a.title.localeCompare(b.title));
    } else {
      jourenysData.sort((a, b) => b.title.localeCompare(a.title));
    }
  } else if (option.type === 'last_modified_at') {
    jourenysData.sort((a, b) => {
      return new Date(b.updatedAt) - new Date(a.updatedAt);
    });
  }
}

export function setActiveStep(step) {
  return (dispatch, getState) => {
    dispatch(actions.activeStep(step));
  };
}

export function requestData(page = 1) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      dispatch(actions.requestData());

      const { params, activeStep: step } = getState();
      const reqParams = {
        query: params.query,
        sort: params.sort,
        filter: {
          type: params.filter.type,
        },
      };

      if (
        params.query &&
        !(
          params.filter.type.includes('User') ||
          params.filter.type.includes('Group')
        )
      ) {
        reqParams.filter.type = params.filter.allTypes;
      }

      request
        .get(routes.ASSIGNABLES[step], { page, ...reqParams })
        .then(response => {
          if (page === 1) {
            dispatch(actions.requestDataSuccess(response));
          } else {
            dispatch(actions.requestNextPageSuccess(response));
          }
          resolve(response);
        });
    });
}

export function requestJourneyData() {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const hasLearningJourneys = hasFeatureFlags([
        'readiness-learning-journeys',
      ]);

      const { params, journeySort } = getState();

      if (hasLearningJourneys) {
        request.get(routes.JOURNEY.GET_PUBLISHED).then(response => {
          let res;

          if (response.journeys) {
            res = response.journeys
              .sort((a, b) => {
                return new Date(b.updatedAt) - new Date(a.updatedAt);
              })
              .map(journey => {
                journey.type = TYPES.JOURNEY;
                return journey;
              });
            if (params.query) {
              res = res.filter(journey =>
                journey.title
                  .toLowerCase()
                  .includes(params.query.toLowerCase()),
              );
            }

            if (journeySort) {
              sortJourneys(res, journeySort);
            }
            dispatch(actions.requestJourneysSuccess(res));
          }
        });
      }
    });
}

export function sortJourneysData(option) {
  return (dispatch, getState) => {
    const journeys = [...getState().journeyData];
    sortJourneys(journeys, option);

    dispatch(actions.updateJourneySort(option));
    dispatch(actions.requestJourneysSuccess(journeys));
  };
}

export function requestNextPage() {
  return (dispatch, getState) => {
    const { page } = getState().pagination;
    dispatch(requestData(page + 1));
  };
}

export function resetData(shouldRequest) {
  return (dispatch, getState) => {
    const hasLearningJourneys = hasFeatureFlags([
      'readiness-learning-journeys',
    ]);

    dispatch(actions.resetData());

    if (shouldRequest) {
      dispatch(requestData());
      if (hasLearningJourneys) dispatch(requestJourneyData());
    }
  };
}

export function requestInitalData() {
  return dispatch => {
    dispatch(resetData());
    dispatch(requestData());
  };
}

export function updateParamQuery(query) {
  return (dispatch, getState) => {
    dispatch(actions.updateQuery(query));

    // Training Content - i.e. Lessons, Paths
    if (getState().contentType === 0) {
      dispatch(requestData());
    }
    // Journey Content
    if (getState().contentType === 1) {
      dispatch(requestJourneyData());
    }
  };
}

export function updateParamSort(option) {
  return (dispatch, getState) => {
    dispatch(actions.updateSort(option));
    dispatch(requestData());
  };
}

export function updateParamFilterType(values) {
  return (dispatch, getState) => {
    dispatch(actions.updateType(values));
    dispatch(requestData());
  };
}

export function updateAssigneesCount() {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const selectedData = getState().selection.assignees.data;
      const getIdentity = pick(['id', 'type']);
      const assignees = map(getIdentity, selectedData);

      request.get(routes.ASSIGNABLES.USER_COUNT, { assignees }).then(data => {
        dispatch(actions.setAssigneesCount(data.count));
      });
    });
}

export function toggleAssignableItem(item) {
  return (dispatch, getState) => {
    if (item.selected) {
      dispatch(actions.deselectItem(item));
    } else {
      item = set('selected', true)(item);
      dispatch(actions.selectItem(item));
    }

    if (isAssignee(item)) {
      dispatch(updateAssigneesCount());
    }
  };
}

export function toggleAssignableEventItem(assignableContent, assigneesList) {
  return (dispatch, getState) => {
    if (assignableContent) {
      const eventContent = assignableContent.map(content => ({
        ...content,
        selected: true,
      }));
      dispatch(actions.selectItem(eventContent));
    }

    if (assigneesList) {
      const eventAssigneesList = assigneesList.map(assignees => ({
        ...assignees,
        selected: true,
      }));
      dispatch(actions.selectItem(eventAssigneesList));
      dispatch(updateAssigneesCount());
    }
  };
}

export function toggleContentType(type) {
  return (dispatch, getState) => {
    dispatch(actions.setContentType(type));
  };
}

export function updateSchedule(changes) {
  return (dispatch, getState) => {
    dispatch(actions.updateSchedule(changes));
  };
}

export function updateExistingAssignmentOption(option) {
  return (dispatch, getState) => {
    dispatch(actions.updateExistingAssignmentOption(option));
  };
}

export function submitAssignment() {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const assignments = buildAssignment(getState);

      if (assignments.content.length || assignments.journeyContent.length) {
        request
          .postJSON(routes.ASSIGNMENT_ASSIGN, assignments)
          .then(data => {
            if (data.success) {
              request.getScript(routes.NOTIFICATIONS_LATEST);
              pubsub.publish(EVENTS.ASSIGNMENT_CREATED, assignments);
              resolve();
            }
          })
          .fail(reject);
      }
    });
}

export function enableSchedulingStep(schedule) {
  return (dispatch, getState) => {
    dispatch(actions.setScheduleStep(schedule));
  };
}
